import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { EnvService } from '@lib/env';
import { HttpClient } from '@angular/common/http';
import { HttpExtras } from '@lib/core';
import { IFileUploadMaxSize } from '@lib/upload-zone/ui';
import {
    AddAttachmentsRequest,
    Attachments,
    Basket,
    BasketItemRequest,
    DeleteAttachmentFileRequest,
    SupplierNote,
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';
import { FrameContractPayload } from '../model/frame-contract-payload';

@Injectable({ providedIn: 'root' })
export class SupplierBasketService {
    private readonly env = inject(EnvService);
    private readonly http = inject(HttpClient);

    private readonly basketSidenavOpen$: BehaviorSubject<boolean>;

    constructor() {
        this.basketSidenavOpen$ = new BehaviorSubject<boolean>(false);
    }

    getSupplierNote(supplierId: string): Observable<SupplierNote> {
        return this.http.get<SupplierNote>(this.env.getBase('basket', 'current', supplierId).toString());
    }

    getBasket(): Observable<Basket> {
        return this.http.get<Basket>(this.env.getBase('basket', 'current').toString(), {
            params: HttpExtras.IGNORE_CACHE,
        });
    }

    updateSupplierComment(comment: string, supplierId: string): Observable<Basket> {
        return this.http.put<Basket>(
            this.env.getBase('basket', 'current', 'supplier-baskets', supplierId, 'comment-for-supplier').toString(),
            { commentForSupplier: comment },
        );
    }

    updatePersonComment(comment: string, supplierId: string): Observable<Basket> {
        return this.http.put<Basket>(
            this.env.getBase('basket', 'current', 'supplier-baskets', supplierId, 'comment-for-approver').toString(),
            { commentForApprover: comment },
        );
    }

    updatePaymentTerm(paymentTermId: number, supplierBasketId: string): Observable<Basket> {
        return this.http.put<Basket>(
            this.env.getBase('basket', 'current', 'supplier-baskets', supplierBasketId, 'payment-term').toString(),
            { paymentTermId: paymentTermId },
        );
    }

    deletePaymentTerm(supplierBasketId: string): Observable<Basket> {
        return this.http.delete<Basket>(
            this.env.getBase('basket', 'current', 'supplier-baskets', supplierBasketId, 'payment-term').toString(),
        );
    }

    /**
     * EPROC-12041: removing the position that carried the last site puts the delivery address back onto
     * the personal default — the recipient's when "Bestellen im Namen von" (UC_OR_1010) is active, which
     * is why the target travels along.
     */
    deleteBasketItem(item: BasketItemRequest, targetUserId?: string | null): Observable<Basket> {
        const uri = this.env.getBase(
            'basket',
            'current',
            'supplier-baskets',
            item.supplierBasketId,
            'basket-items',
            item.basketItem.id,
        );
        if (targetUserId) {
            uri.query['targetUserId'] = targetUserId;
        }
        return this.http.delete<Basket>(uri.toString());
    }

    addAttachments(data: AddAttachmentsRequest): Observable<Attachments[]> {
        const supplierBasketId = data.supplierBasketId;
        return this.http.post<Attachments[]>(
            this.env.getBase('basket', 'current', 'supplier-baskets', supplierBasketId, 'attachments').toString(),
            data.attachments,
            { params: new HttpExtras({ customErrorhandling: false }) },
        );
    }

    deleteAttachmentFile(attachmentFile: DeleteAttachmentFileRequest): Observable<Attachments> {
        const supplierBasketId = attachmentFile.supplierBasketId;
        const attachmentId = attachmentFile.attachmentId;
        return this.http.delete<Attachments>(
            this.env
                .getBase('basket', 'current', 'supplier-baskets', supplierBasketId, 'attachments', attachmentId)
                .toString(),
        );
    }

    getMaxUploadSize(): Observable<IFileUploadMaxSize> {
        return this.http.get<IFileUploadMaxSize>(this.env.getBase('file', 'limit').toString());
    }

    updateFrameContracts(supplierBasketId: string, payload: FrameContractPayload): Observable<Basket> {
        const url = this.env
            .getBase('basket', 'current', 'supplier-baskets', supplierBasketId, 'frame-contract')
            .toString();

        return this.http.put<Basket>(url, payload);
    }

    deleteFrameContracts(supplierBasketId: string): Observable<Basket> {
        const url = this.env
            .getBase('basket', 'current', 'supplier-baskets', supplierBasketId, 'frame-contract')
            .toString();

        return this.http.delete<Basket>(url);
    }
}
